/**
 * useUploader — uploads captured frames to Azure Blob via SAS URLs.
 *
 * Handles: batch SAS URL fetching, parallel uploads (4 concurrent),
 * progress tracking, retry logic, scan completion, and resume after interruption.
 *
 * Frames are persisted in IndexedDB by useCamera. This hook tracks upload progress
 * there so interrupted uploads can be resumed after browser kill / phone call.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { createScan, getUploadUrls, uploadFrame, markScanComplete, getScan } from '@services/scanApi'
import {
  getFramesBySession,
  getPendingFrames,
  markFrameUploaded,
  markFrameFailed,
  updateSession,
  deleteSessionAndFrames,
} from '@services/frameStore'
import type { ScanSession, PersistedFrame } from '@services/frameStore'
import { apiUrl } from '../config'
import { getAuthHeaders } from '../stores/authStore'
import type { Scan, UploadUrl } from '@app-types/scan'

interface FramePair {
  blob: Blob     // Low-res for COLMAP/navigation
  hdBlob: Blob   // HD for zoom display
}

interface UploadProgress {
  total: number
  uploaded: number
  failed: number
  percent: number
}

type UploadPhase = 'idle' | 'creating' | 'uploading' | 'completing' | 'polling' | 'done' | 'error'

const CONCURRENT_UPLOADS = 4
const POLL_INTERVAL_MS = 5000
const MAX_RETRIES = 5

export function useUploader() {
  const [phase, setPhase] = useState<UploadPhase>('idle')
  const [progress, setProgress] = useState<UploadProgress>({ total: 0, uploaded: 0, failed: 0, percent: 0 })
  const [scan, setScan] = useState<Scan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<number | null>(null)
  const isUploadingRef = useRef(false)
  const abortedRef = useRef(false)

  // Warn user before closing browser during upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploadingRef.current) {
        e.preventDefault()
        e.returnValue = 'Upload in progress. Are you sure you want to leave?'
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // Auto-restart stalled uploads when page becomes visible again (e.g. after phone call)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isUploadingRef.current && abortedRef.current) {
        console.log('[useUploader] Page became visible, upload was stalled — will retry on next batch')
        abortedRef.current = false
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Stop status polling
  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Start polling for processing completion
  const startPolling = useCallback(
    (scanId: string, shopId: string, location?: { lat: number; lng: number } | null) => {
      setPhase('polling')
      pollRef.current = window.setInterval(async () => {
        try {
          const updated = await getScan(scanId)
          setScan(updated)
          if (updated.status === 'ready' || updated.status === 'failed') {
            stopPolling()
            setPhase('done')

            // Auto-update shop location if null (only on successful processing)
            if (updated.status === 'ready' && location) {
              try {
                const shopRes = await fetch(apiUrl(`/api/shops/${shopId}`), { headers: getAuthHeaders() })
                if (shopRes.ok) {
                  const shop = await shopRes.json()
                  if (shop.location === null) {
                    await fetch(apiUrl(`/api/shops/${shopId}`), {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                      body: JSON.stringify({ location }),
                    })
                    console.log('✓ [useUploader] Updated shop location from scan:', location)
                  }
                }
              } catch (err) {
                console.warn('⚠️ [useUploader] Failed to update shop location:', err)
              }
            }
          }
        } catch {
          // Keep polling on transient errors
        }
      }, POLL_INTERVAL_MS)
    },
    [stopPolling],
  )

  /**
   * Core upload logic: uploads a list of frame blobs using SAS URLs, tracking progress in IndexedDB.
   * Used by both fresh upload and resume flows.
   */
  const uploadFrameBatch = useCallback(
    async (
      frames: PersistedFrame[],
      urls: UploadUrl[],
      scanSessionId: string,
      startUploaded: number,
    ): Promise<{ uploaded: number; failed: number }> => {
      const totalOps = frames.length * 2
      let uploaded = startUploaded * 2 // already uploaded (low-res + HD)
      let failed = 0
      const totalWithPrev = (startUploaded + frames.length) * 2

      setProgress({ total: totalWithPrev, uploaded, failed: 0, percent: Math.round((uploaded / totalWithPrev) * 100) })

      // Build a map from frameIndex to URL info
      const urlByIndex = new Map<number, UploadUrl>()
      for (const u of urls) urlByIndex.set(u.frame_index, u)

      // Process in batches of CONCURRENT_UPLOADS
      for (let i = 0; i < frames.length; i += CONCURRENT_UPLOADS) {
        const batch = frames.slice(i, i + CONCURRENT_UPLOADS)
        const promises = batch.map(async (frame) => {
          const urlInfo = urlByIndex.get(frame.frameIndex)
          if (!urlInfo) {
            console.error(`[useUploader] No SAS URL for frame index ${frame.frameIndex}`)
            failed += 2
            await markFrameFailed(frame.id)
            return
          }

          // Upload low-res frame
          try {
            await uploadFrame(urlInfo.upload_url, frame.blob, MAX_RETRIES)
            uploaded++
          } catch {
            failed++
            console.error(`Failed to upload low-res frame ${frame.frameIndex}`)
          }
          setProgress({ total: totalWithPrev, uploaded, failed, percent: Math.round(((uploaded + failed) / totalWithPrev) * 100) })

          // Upload HD frame
          if (urlInfo.hd_upload_url) {
            try {
              await uploadFrame(urlInfo.hd_upload_url, frame.hdBlob, MAX_RETRIES)
              uploaded++
            } catch {
              failed++
              console.error(`Failed to upload HD frame ${frame.frameIndex}`)
            }
            setProgress({ total: totalWithPrev, uploaded, failed, percent: Math.round(((uploaded + failed) / totalWithPrev) * 100) })
          }

          // Mark frame as uploaded in IndexedDB
          if (failed === 0) {
            await markFrameUploaded(frame.id)
            await updateSession(scanSessionId, { uploadedCount: Math.floor(uploaded / 2) })
          } else {
            await markFrameFailed(frame.id)
          }
        })
        await Promise.all(promises)
      }

      return { uploaded: Math.floor(uploaded / 2), failed }
    },
    [],
  )

  // ── Fresh upload (from capture) ──
  const upload = useCallback(
    async (
      framePairs: FramePair[],
      shopId: string,
      sectionName: string,
      sectionId?: string,
      location?: { lat: number; lng: number } | null,
      scanSessionId?: string,
    ) => {
      try {
        setError(null)
        setPhase('creating')
        isUploadingRef.current = true
        abortedRef.current = false

        // Use provided sectionId or generate from name
        const finalSectionId = sectionId || ('sec_' + sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''))

        // Step 1: Create scan (with location if available)
        const newScan = await createScan({
          shop_id: shopId,
          section_id: finalSectionId,
          section_name: sectionName,
          frame_count: framePairs.length,
          location: location || null,
        })
        setScan(newScan)

        // Update IndexedDB session with server scan ID
        if (scanSessionId) {
          await updateSession(scanSessionId, { serverScanId: newScan.id, status: 'uploading' })
        }

        // Step 2: Get SAS upload URLs (includes both standard + HD URLs)
        setPhase('uploading')
        const { urls } = await getUploadUrls(newScan.id, framePairs.length)

        // Step 3: Get frames from IndexedDB if session is available, otherwise use in-memory blobs
        let framesToUpload: PersistedFrame[]
        if (scanSessionId) {
          framesToUpload = await getFramesBySession(scanSessionId)
          // Sort by frameIndex to match URL order
          framesToUpload.sort((a, b) => a.frameIndex - b.frameIndex)
        } else {
          // Fallback: create temporary PersistedFrame objects from in-memory framePairs
          framesToUpload = framePairs.map((fp, idx) => ({
            id: `temp_${idx + 1}`,
            scanSessionId: 'temp',
            frameIndex: idx + 1,
            blob: fp.blob,
            hdBlob: fp.hdBlob,
            status: 'pending' as const,
            createdAt: Date.now(),
          }))
        }

        const { failed } = await uploadFrameBatch(framesToUpload, urls, scanSessionId || 'temp', 0)

        isUploadingRef.current = false

        if (failed > 0) {
          console.warn(`[useUploader] ${failed} frame operations failed`)
        }

        // Step 4: Mark scan complete
        setPhase('completing')
        const completed = await markScanComplete(newScan.id)
        setScan(completed)

        // Update IndexedDB session
        if (scanSessionId) {
          await updateSession(scanSessionId, { status: 'completed' })
        }

        // Step 5: Poll for processing completion
        startPolling(newScan.id, shopId, location)

        // Step 6: Clean up IndexedDB after successful upload
        if (scanSessionId) {
          // Delay cleanup slightly so polling can start
          setTimeout(() => {
            deleteSessionAndFrames(scanSessionId).catch(() => {})
          }, 2000)
        }
      } catch (err) {
        isUploadingRef.current = false
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setError(msg)
        setPhase('error')

        // Mark session as failed but keep frames for retry
        if (scanSessionId) {
          await updateSession(scanSessionId, { status: 'ready' }).catch(() => {})
        }
      }
    },
    [stopPolling, uploadFrameBatch, startPolling],
  )

  // ── Resume interrupted upload ──
  const resume = useCallback(
    async (session: ScanSession) => {
      try {
        setError(null)
        isUploadingRef.current = true
        abortedRef.current = false

        // Get remaining pending frames from IndexedDB
        const pendingFrames = await getPendingFrames(session.id)
        if (pendingFrames.length === 0) {
          setError('No pending frames found to resume')
          setPhase('error')
          isUploadingRef.current = false
          return
        }

        pendingFrames.sort((a, b) => a.frameIndex - b.frameIndex)
        const alreadyUploaded = session.frameCount - pendingFrames.length

        // If we don't have a server scan ID yet, create one
        let serverScanId = session.serverScanId
        if (!serverScanId) {
          setPhase('creating')
          const newScan = await createScan({
            shop_id: session.shopId,
            section_id: session.sectionId,
            section_name: session.sectionName,
            frame_count: session.frameCount,
            location: session.location || null,
          })
          setScan(newScan)
          serverScanId = newScan.id
          await updateSession(session.id, { serverScanId, status: 'uploading' })
        }

        // Request SAS URLs for ALL frames (backend generates for frame_count starting at 1)
        setPhase('uploading')
        const { urls } = await getUploadUrls(serverScanId, session.frameCount)
        await updateSession(session.id, { status: 'uploading' })

        // Upload only the pending frames
        const { failed } = await uploadFrameBatch(pendingFrames, urls, session.id, alreadyUploaded)

        isUploadingRef.current = false

        if (failed > 0) {
          console.warn(`[useUploader] Resume: ${failed} frame operations failed`)
        }

        // Mark scan complete
        setPhase('completing')
        const completed = await markScanComplete(serverScanId)
        setScan(completed)
        await updateSession(session.id, { status: 'completed' })

        // Poll for processing
        startPolling(serverScanId, session.shopId, session.location)

        // Clean up IndexedDB
        setTimeout(() => {
          deleteSessionAndFrames(session.id).catch(() => {})
        }, 2000)
      } catch (err) {
        isUploadingRef.current = false
        const msg = err instanceof Error ? err.message : 'Resume upload failed'
        setError(msg)
        setPhase('error')

        // Keep frames for another retry
        await updateSession(session.id, { status: 'ready' }).catch(() => {})
      }
    },
    [uploadFrameBatch, startPolling],
  )

  // Reset state
  const reset = useCallback(() => {
    stopPolling()
    isUploadingRef.current = false
    abortedRef.current = false
    setPhase('idle')
    setProgress({ total: 0, uploaded: 0, failed: 0, percent: 0 })
    setScan(null)
    setError(null)
  }, [stopPolling])

  return { phase, progress, scan, error, upload, resume, reset }
}
